import React from "react";
import { Tooltip } from "@/components/common/Tooltip";

export interface SegmentedControlOption<T> {
  value: T;
  label: string;
  /** Kept in place, greyed; `hint` names the reason on hover. */
  disabled?: boolean;
  hint?: string;
  /** A quiet number after the label, such as how many the option holds. */
  count?: number;
  /** Something went wrong with what the option stands for. */
  warn?: boolean;
  /** What the option stands for, on hover. */
  tooltip?: React.ReactNode;
  /** The pointer can move into the tooltip, to read or scroll a long one. */
  interactiveTooltip?: boolean;
  /** A small mark after the label, such as a check for one that is done. */
  mark?: React.ReactNode;
  /** An icon before the label. */
  icon?: React.ReactNode;
}

export interface SegmentedControlProps<T> {
  options: SegmentedControlOption<T>[];
  value: T;
  onChange: (v: T) => void;
  /** If set, shows a green dot on the active option when it matches autoValue */
  autoValue?: T | null;
  /** The option under the pointer, or `null` once it leaves the whole control,
   *  so crossing from one segment to the next never reports a gap. */
  onHover?: (v: T | null) => void;
  /** Clicking the chosen option clears it, so it stays a pointer. */
  deselectable?: boolean;
}

export function SegmentedControl<T>({
  options,
  value,
  onChange,
  autoValue,
  onHover,
  deselectable = false,
}: SegmentedControlProps<T>) {
  return (
    <div
      className="inline-flex h-full border border-border rounded-app overflow-hidden"
      onMouseLeave={onHover && (() => onHover(null))}
    >
      {options.map((opt, i) => {
        const isActive = opt.value === value;
        const isAuto = isActive && autoValue != null && opt.value === autoValue;
        const pointer =
          isActive && !deselectable ? "cursor-default" : "cursor-pointer";
        const tone = opt.disabled
          ? `bg-transparent text-fg-muted/50 ${opt.hint ? "cursor-pointer" : "cursor-not-allowed"}`
          : opt.warn
            ? `bg-warning-bg text-warning ${pointer} ${isActive ? "font-medium" : ""}`
            : isActive
              ? `bg-accent-subtle text-accent font-medium ${pointer}`
              : "bg-transparent text-fg-muted hover:bg-bg-hover hover:text-fg cursor-pointer";
        const button = (
          <button
            type="button"
            onClick={() => !opt.disabled && onChange(opt.value)}
            onMouseEnter={onHover && (() => onHover(opt.value))}
            aria-pressed={isActive}
            aria-disabled={opt.disabled || undefined}
            aria-label={opt.hint ? opt.label : undefined}
            // The invisible bold copy holds the width, so selecting never resizes the control.
            data-label={opt.label}
            className={`h-full px-2 text-(length:--font-size) whitespace-nowrap transition-colors duration-100 after:block after:h-0 after:overflow-hidden after:invisible after:font-medium after:content-[attr(data-label)] ${tone}`}
          >
            {opt.icon && (
              <span
                className={`${opt.label ? "mr-1" : ""} inline-flex align-middle relative -top-px`}
              >
                {opt.icon}
              </span>
            )}
            {opt.label}
            {opt.count !== undefined && (
              <span className="ml-1 font-mono font-normal text-(length:--font-size-small) tabular-nums">
                {opt.count}
              </span>
            )}
            {opt.mark && (
              <span className="ml-1 inline-flex align-middle relative -top-px">
                {opt.mark}
              </span>
            )}
            {isAuto && (
              <span className="inline-block w-1.5 h-1.5 rounded-full bg-success ml-1 align-middle relative -top-px" />
            )}
          </button>
        );
        return (
          <span
            key={String(opt.value)}
            className={`flex ${i < options.length - 1 ? "border-r border-border" : ""}`}
          >
            {opt.disabled && opt.hint ? (
              <Tooltip title={opt.hint} placement="top">
                {button}
              </Tooltip>
            ) : opt.tooltip ? (
              <Tooltip
                title={opt.tooltip}
                placement="top"
                interactive={opt.interactiveTooltip}
              >
                {button}
              </Tooltip>
            ) : (
              button
            )}
          </span>
        );
      })}
    </div>
  );
}
